// context/SocketContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../context/AuthContext'; // <- adapte le chemin si besoin

// --- Types ---
export type NotificationItem = {
  _id?: string;
  id?: string;
  recipient?: string;
  message: string;
  type?:
    | 'mission_assigned'
    | 'status_update'
    | 'new_message'
    | 'system'
    | 'quote_response'
    | 'report_created'
    | 'report_sent'
    | string;
  isRead?: boolean;
  createdAt?: string;
  relatedEntity?: { id: string; type: 'Mission' | 'Quote' | 'Request' | 'Report' | string };
  [k: string]: any;
};

interface SocketContextType {
  socket: Socket | null;
  notifications: NotificationItem[];
  // Helpers centrés Provider
  pushNotification: (n: NotificationItem) => void;
  replaceNotifications: (list: NotificationItem[]) => void;
  markOneAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within a SocketProvider');
  return ctx;
};

export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  // --- Socket unique (jamais recréé) ---
  const socketRef = useRef<Socket | null>(null);
  const [socketState, setSocketState] = useState<Socket | null>(null);
  const initOnce = useRef(false);

  // --- État notifications global ---
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const SOCKET_URL = import.meta.env.VITE_SOCKET_URL ?? 'http://localhost:3002';

  // Init socket UNE SEULE fois (même sous React.StrictMode)
  useEffect(() => {
    if (initOnce.current) return;
    initOnce.current = true;

    const s = io(SOCKET_URL, {
      autoConnect: false,          // on contrôle nous-mêmes la connexion
      transports: ['websocket'],   // évite le fallback polling (source de "reload" perçu)
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 600,
    });

    socketRef.current = s;
    setSocketState(s);

    // Réception d’une notif => maj locale (pas de reload page)
    const onNew = (n: NotificationItem) => {
      // setNotifications(prev => [{ ...n, id: n._id ?? n.id }, ...prev]);
    };

    s.on('newNotification', onNew);

    return () => {
      s.off('newNotification', onNew);
      s.close();
      socketRef.current = null;
      setSocketState(null);
    };
  }, [SOCKET_URL]);

  // (Re)auth sans recréer le socket → pas de "reload"
  useEffect(() => {
    const s = socketRef.current;
    if (!s) return;

    if (isAuthenticated && user?.id) {
      // Compat server: certains lisent handshake.auth, d’autres query
      (s as any).auth = { userId: user.id };
      // @ts-ignore
      if (s.io?.opts) s.io.opts.query = { userId: user.id };

      if (!s.connected) s.connect();
    } else {
      if (s.connected) s.disconnect();
      setNotifications([]); // reset après logout
    }
  }, [isAuthenticated, user?.id]);

  // --- Helpers exposés ---
  const pushNotification = (n: NotificationItem) =>
    setNotifications(prev => [{ ...n, id: n._id ?? n.id }, ...prev]);

  const replaceNotifications = (list: NotificationItem[]) =>
    setNotifications(list.map(n => ({ ...n, id: n._id ?? n.id })));

  const markOneAsRead = (id: string) =>
    setNotifications(prev =>
      prev.map(n => (String(n._id ?? n.id) === String(id) ? { ...n, isRead: true } : n)),
    );

  const markAllAsRead = () =>
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));

  const removeNotification = (id: string) =>
    setNotifications(prev => prev.filter(n => String(n._id ?? n.id) !== String(id)));

  return (
    <SocketContext.Provider
      value={{
        socket: socketState,
        notifications,
        pushNotification,
        replaceNotifications,
        markOneAsRead,
        markAllAsRead,
        removeNotification,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
